import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { hideAd, hideSimilarAds } from "../../hook/useAdsApi";

// Reason codes for POST ads/preferences/hide and .../hide-similar (ad-preference.validations.js
// / configuration/constant.js's adHideReasons) - there's no reasons endpoint for these like
// Report Ad has, so the fixed list is mirrored here from the API's swagger description.
const HIDE_REASONS = [
  { code: "NOT_INTERESTED", display_name: "Not interested" },
  { code: "ALREADY_PURCHASED", display_name: "Already purchased" },
  { code: "IRRELEVANT", display_name: "Irrelevant" },
  { code: "REPETITIVE", display_name: "Repetitive" },
  { code: "DONT_WANT_TO_SEE", display_name: "Don't want to see this" },
  { code: "OTHER", display_name: "Other" },
];

// Shared modal for "Hide this ad" (mode="hide", reason optional) and "Hide similar ads"
// (mode="hide-similar", reason required), styled after ReportAdModal.js's bottom-sheet pattern.
export default function AdPreferenceModal({ isOpen, onClose, mode, ad, onSuccess }) {
  const isSimilar = mode === "hide-similar";
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setError("");
  }, [isOpen, ad?.id, mode]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSimilar && !reason) {
      setError("Please select a reason.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const categoryId = ad?.product?.category_id;
      const body = {
        ad_id: ad.id,
        ...(ad?.campaign_id ? { campaign_id: ad.campaign_id } : {}),
        ...(ad?.ad_set_id ? { ad_set_id: ad.ad_set_id } : {}),
        ...(reason ? { reason_code: reason } : {}),
        timestamp: new Date().toISOString(),
      };

      const result = isSimilar
        ? await hideSimilarAds({ ...body, ...(categoryId ? { category_id: categoryId } : {}) })
        : await hideAd(body);

      toast.success(result?.message || (isSimilar ? "Similar ads preference saved" : "Ad hidden successfully"));
      onSuccess?.();
      onClose();
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.response?.data?.message;
      if (status === 401) {
        setError("Please sign in to continue.");
      } else if (status === 404) {
        setError(message || "This ad could not be found.");
      } else if (status === 400) {
        setError(message || "Invalid request.");
      } else {
        setError(message || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-50 flex justify-center items-end md:items-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-2xl md:rounded-2xl p-6 shadow-lg relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-black text-xl font-bold"
          aria-label="Close"
        >
          ✕
        </button>

        <h2 className="text-xl font-bold mb-1 text-[#BE1F2E]">
          {isSimilar ? "Hide similar ads" : "Hide this ad"}
        </h2>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <p className="text-sm text-gray-600">
            {isSimilar
              ? "Tell us why so we can show you fewer ads like this."
              : "You won't see this ad again. Let us know why (optional)."}
          </p>

          <div>
            <label className="block text-sm font-bold text-black mb-2">
              Reason{!isSimilar && " (optional)"}
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={submitting}
              className="w-full h-12 px-3 rounded-xl border border-[#095228] bg-white text-sm focus:outline-none"
            >
              <option value="">{isSimilar ? "Select a reason" : "No specific reason"}</option>
              {HIDE_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.display_name}
                </option>
              ))}
            </select>
          </div>

          {error && <p className="text-red-500 text-xs">{error}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#BE1F2E] text-white h-12 rounded-full font-bold text-sm disabled:opacity-50"
          >
            {submitting ? "Saving..." : isSimilar ? "Save preference" : "Hide ad"}
          </button>
        </form>
      </div>
    </div>
  );
}
